/* eslint-disable @typescript-eslint/no-shadow */
import '@orobix/label-studio/build/static/css/main.css'

import {
  faChevronLeft,
  faChevronRight,
  faClose,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from '@orobix/orobix-fe-components'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { AnnotationType } from '../../../types/annotation'
import EditLabelModal from '../../EditLabelModal/EditLabelModal'
import AnnotatorInfoModal from '../AnnotatorInfoModal'
import { AnnotationLabel } from '../types'
import useStyles from './Topbar.styles'

type TopbarProps = {
  title: string
  productId: number
  isLoading: boolean
  isSaving: boolean
  onClose?: () => void
  onNext?: () => void
  onPrev?: () => void
  onSkip?: () => void
  onSave?: () => void
  onLabelUpdated?: (labels: Array<AnnotationLabel>) => void
  filename?: string
  annotationType: AnnotationType
}

const useHotkeys = (
  onClose?: () => void,
  onNext?: () => void,
  onPrev?: () => void
) => {
  const handleKeyUp = useCallback(
    (e: KeyboardEvent): void => {
      const hotkeysHandlers = {
        Escape: () => {
          e.preventDefault()
          if (onClose) onClose()
          window.removeEventListener('keyup', handleKeyUp)
        },
        ArrowRight: () => {
          e.preventDefault()
          if (onNext) onNext()
        },
        ArrowLeft: () => {
          e.preventDefault()
          if (onPrev) onPrev()
        },
      }

      const pressedKey = e.key as keyof typeof hotkeysHandlers

      if (hotkeysHandlers[pressedKey]) {
        hotkeysHandlers[pressedKey]()
      }
    },
    [onClose, onNext, onPrev]
  )

  useEffect(() => {
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [handleKeyUp])

  return null
}

export const Topbar = ({
  title,
  productId,
  isLoading,
  isSaving,
  onClose,
  onNext,
  onPrev,
  onLabelUpdated,
  onSkip,
  onSave,
  filename,
  annotationType,
}: TopbarProps) => {
  const classes = useStyles()
  const { t } = useTranslation()

  useHotkeys(onClose, onNext, onPrev)

  return (
    <div className={classes.topbar}>
      <div className={classes.prevNext}>
        <Button
          label={t('Close')}
          title={isSaving ? t('Saving annotations, please wait.') : t('Close')}
          icon={<FontAwesomeIcon icon={faClose} />}
          buttonType="button"
          type="blank-default"
          hideLabel
          onClick={onClose}
          disabled={isLoading || isSaving}
          data-testid="annotatorCloseButton"
        />

        <Button
          label={t('Previous image')}
          title={t('Previous image')}
          icon={<FontAwesomeIcon icon={faChevronLeft} />}
          buttonType="button"
          type="blank-default"
          hideLabel
          onClick={onPrev}
          disabled={isLoading}
          data-testid="annotatorPreviousButton"
        />

        <Button
          label={t('Next image')}
          title={t('Next image')}
          icon={<FontAwesomeIcon icon={faChevronRight} />}
          buttonType="button"
          type="blank-default"
          hideLabel
          onClick={onNext}
          disabled={isLoading}
          data-testid="annotatorNextButton"
        />

        <div data-testid="annotatorEditColorButton">
          {annotationType !== 'Tag' && (
            <EditLabelModal
              productId={productId}
              onLabelUpdated={onLabelUpdated}
            />
          )}
        </div>
      </div>
      {filename && (
        <div className={classes.titleBox}>
          <span className={classes.title} data-testid="annotatorFilename">
            {filename}
          </span>
        </div>
      )}
      <div className={classes.titleBox} data-testid="annotatorTitle">
        <span className={classes.title}>{title}</span>
      </div>
      <div className={classes.flex1} />
      <div className={classes.infoIcon} data-testid="annotatorHowToUseButton">
        <AnnotatorInfoModal annotationType={annotationType} />
      </div>
      <div className={classes.controls}>
        {onSkip && (
          <button
            className={classes.skipButton}
            type="button"
            onClick={onSkip}
            disabled={isLoading}
            data-testid="annotatorSkipButton"
          >
            {t('Skip')}
          </button>
        )}
        {onSave && (
          <button
            className={classes.saveButton}
            type="button"
            onClick={onSave}
            disabled={isLoading}
            data-testid="annotatorSaveButton"
          >
            {t('Save')}
          </button>
        )}
      </div>
    </div>
  )
}
